import * as React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useBlockRuleById, useUpdateBlockRule, BlockRule } from '@/lib/api/blockRules';
import { BlockRuleForm, BlockRuleFormValues } from '../components/block-rule-form';
import { toMutationInput } from '../utils';

function BlockRuleEditContent({ blockRule }: { blockRule: BlockRule }) {
    const { orgRoutes, navigate } = useOrgNavigate();
    const { mutateAsync: updateBlockRule, isPending } = useUpdateBlockRule();

    const handleSubmit = React.useCallback(
        async (values: BlockRuleFormValues) => {
            const input = toMutationInput(values);
            await updateBlockRule({ id: blockRule.id, data: input });
            navigate(orgRoutes.BLOCK_RULES());
        },
        [blockRule.id, updateBlockRule, navigate, orgRoutes],
    );

    const handleCancel = React.useCallback(() => {
        navigate(orgRoutes.BLOCK_RULES());
    }, [navigate, orgRoutes]);

    return (
        <div className="flex flex-col space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={handleCancel}>
                        <ArrowLeft className="size-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Edit block rule</h1>
                        <p className="text-muted-foreground text-sm">
                            Update the conditions for this fraud block rule.
                        </p>
                    </div>
                </div>
                <div className="flex flex-row gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" size="sm" form="block-rule-form" disabled={isPending}>
                        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save changes
                    </Button>
                </div>
            </div>
            <Card>
                <CardContent className="pt-6">
                    <BlockRuleForm
                        blockRule={blockRule}
                        onSubmit={handleSubmit}
                        isSubmitting={isPending}
                    />
                </CardContent>
            </Card>
        </div>
    );
}

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(BlockRuleDetailsPage, PERMISSIONS.PROCESSORS_VIEW);

function BlockRuleDetailsPage() {
    const { id: blockRuleId } = useParams();
    const { orgRoutes, navigate } = useOrgNavigate();

    const { data: blockRule, isLoading, error } = useBlockRuleById(blockRuleId || '');

    if (!blockRuleId) return null;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error || !blockRule) {
        return (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <p className="text-muted-foreground">Block rule not found</p>
                <Button variant="outline" onClick={() => navigate(orgRoutes.BLOCK_RULES())}>
                    Back to block rules
                </Button>
            </div>
        );
    }

    return <BlockRuleEditContent blockRule={blockRule} />;
}
